import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom-v5-compat';

import { ROUTES } from './routes';
import { PrivateRoute } from './views/PrivateRoute';
import { PublicRoute } from './components/PublicRoute';
import SignUpPage from './views/SignUpPage';
import SignInPage from './views/SignInPage';
import ResetPasswordPage from './views/ResetPasswordPage';
import AcceptInvitePage from './views/AcceptInvitePage';
import LinkedinAuthCallback from './views/LinkedinAuthCallback';
import GoogleAuthCallback from './views/GoogleAuthCallback';
import TwitterAuthCallback from './views/TwitterAuthCallback';
import OAuthCallback from './views/OAuthCallback';
import VerifyEmailCallback from './views/VerifyEmailCallback/Container';
import { VerifyAppsumoCallback } from './views/VerifyAppsumoCallback';
import NotVerifiedPage from './views/NotVerifiedPage';
import DemoPage from './views/DemoPage';
import { ProjectsPage } from './views/ProjectsPage';
import { PersonasPage } from './views/PersonasPage';
import { ProjectCreatePage } from './views/ProjectCreatePage';
import ProjectPage from './views/ProjectPage';
import SettingsPage from './views/SettingsPage';
import MobileDevicePage from './views/MobileDevicePage/Container';
import LockedOutPage from './views/LockedOutPage';
import { SubscriptionPage } from './views/SubscriptionPage';
import { CheckoutPage } from './views/CheckoutPage';
import RootPage from './views/RootPage';
import { ExternalSignupIframe } from './views/ExternalSignupIframe';
import { ExternalSignupPage } from './views/ExternalSignupPage';
import { DashboardPage } from './views/DashboardPage';
import { ExternalFreeGeneratorIframe } from './views/ExternalFreeGeneratorIframe';
import { useLocationBlocker } from './hooks/useLocationBlocker';
import { SharedPage } from './views/SharedPage';
import { ExternalAnycopyWidget } from './views/ExternalAnycopyWidget';
import { PerformancePage } from './views/PerformancePage';
import { BrandGuidelinesPage } from './views/BrandGuidelinesPage';
import { BrandBriefsPage } from './views/BrandBriefsPage';
import { ExternalInfinityExtensionIframe } from './views/ExternalInfinityExtensionIframe';
import { CustomFormulasPage } from './views/CustomFormulasPage';
import { ToneOfVoicePage } from './views/ToneOfVoicePage';
import { useCancelPerformanceWaitRequests, useRouteShouldHideOverflow } from './services';

// `react-router-dom-v5-compat`(it is official migration package) is used because `react-router-last-location`
// doesn't support react-router v6 so we need to use react-router v5
// After `react-router-last-location` will migrate to react-router v6, the following should be done:
//   1. uninstall `react-router-dom-v5-compat`, `react-router` and `react-router-dom` from package.json
//   2. install `react-router-dom`
//   3. remove `<CompatRouter>` from `App.js`
//   4. rewrite useLocationBlocker() using react-router v6
// Migration guide https://reactrouter.com/docs/en/v6/upgrading/v5

export const AppRouting = () => {
  const routeShouldHideOverflow = useRouteShouldHideOverflow();

  useLocationBlocker();
  useCancelPerformanceWaitRequests();

  return (
    <Routes>
      <Route
        path={ROUTES.SIGN_UP}
        element={
          <PublicRoute>
            <SignUpPage />
          </PublicRoute>
        }
      />
      <Route
        path={ROUTES.SIGN_UP_DEPRECATED}
        element={
          <Navigate
            to={ROUTES.SIGN_UP}
            replace
          />
        }
      />

      <Route
        path={ROUTES.SIGN_IN}
        element={
          <PublicRoute>
            <SignInPage />
          </PublicRoute>
        }
      />
      <Route
        path={ROUTES.SIGN_IN_DEPRECATED}
        element={
          <Navigate
            to={ROUTES.SIGN_IN}
            replace
          />
        }
      />

      <Route
        path={ROUTES.RESET_PASSWORD}
        element={
          <PublicRoute displayNavigation>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path={ROUTES.ACCEPT_INVITE}
        element={
          <PublicRoute displayNavigation>
            <AcceptInvitePage />
          </PublicRoute>
        }
      />
      <Route
        path={ROUTES.LINKEDIN_AUTH_CALLBACK}
        element={
          <PublicRoute displayNavigation>
            <LinkedinAuthCallback />
          </PublicRoute>
        }
      />
      <Route
        path={ROUTES.GOOGLE_AUTH_CALLBACK}
        element={
          <PublicRoute displayNavigation>
            <GoogleAuthCallback />
          </PublicRoute>
        }
      />
      <Route
        path={ROUTES.TWITTER_AUTH_CALLBACK}
        element={
          <PublicRoute displayNavigation>
            <TwitterAuthCallback />
          </PublicRoute>
        }
      />
      {[ROUTES.OAUTH_CALLBACK, ROUTES.SAML_CALLBACK].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <PublicRoute displayNavigation>
              <OAuthCallback />
            </PublicRoute>
          }
        />
      ))}
      <Route
        path={ROUTES.VERIFY_EMAIL_CALLBACK}
        element={
          <PublicRoute displayNavigation>
            <VerifyEmailCallback />
          </PublicRoute>
        }
      />
      <Route
        path={ROUTES.VERIFY_APPSUMO_CALLBACK}
        element={
          <PublicRoute displayNavigation>
            <VerifyAppsumoCallback />
          </PublicRoute>
        }
      />
      <Route
        path={ROUTES.EXTERNAL_SIGNUP_IFRAME}
        element={
          <PublicRoute displayNavigation>
            <ExternalSignupIframe />
          </PublicRoute>
        }
      />
      <Route
        path={ROUTES.EXTERNAL_SIGNUP}
        element={
          <PublicRoute displayNavigation>
            <ExternalSignupPage />
          </PublicRoute>
        }
      />
      <Route
        path={ROUTES.EXTERNAL_FREE_GENERATOR}
        element={
          <PublicRoute>
            <ExternalFreeGeneratorIframe />
          </PublicRoute>
        }
      />
      <Route
        path={`${ROUTES.SHARED}/:shareDocumentId`}
        element={
          <PublicRoute>
            <SharedPage />
          </PublicRoute>
        }
      />
      <Route
        path={ROUTES.EXTERNAL_ANYCOPY_WIDGET}
        element={
          <PublicRoute>
            <ExternalAnycopyWidget />
          </PublicRoute>
        }
      />

      <Route
        path={ROUTES.DASHBOARD}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <DashboardPage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.NOT_VERIFIED}
        element={
          <PrivateRoute>
            <NotVerifiedPage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.EXTENSION_DEMO}
        element={
          <PrivateRoute>
            <DemoPage />
          </PrivateRoute>
        }
      />
      <Route
        path={`${ROUTES.PERFORMANCE}/:view`}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
            hideOverflow={routeShouldHideOverflow}
          >
            <PerformancePage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.BRAND_GUIDELINES}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <BrandGuidelinesPage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.BRAND_BRIEFS}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <BrandBriefsPage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.CUSTOM_FORMULAS}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <CustomFormulasPage />
          </PrivateRoute>
        }
      />

      <Route
        path={ROUTES.TONE_OF_VOICE}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <ToneOfVoicePage />
          </PrivateRoute>
        }
      />

      <Route
        path={ROUTES.PROJECTS}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
            exact
          >
            <ProjectsPage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.PERSONAS}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <PersonasPage />
          </PrivateRoute>
        }
      />
      {[
        `${ROUTES.PROJECTS_CREATE}/:projectType/:projectId`,
        `${ROUTES.PROJECTS_CREATE}/:projectType`,
        ROUTES.PROJECTS_CREATE,
      ].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <PrivateRoute>
              <ProjectCreatePage />
            </PrivateRoute>
          }
        />
      ))}
      <Route
        path={`${ROUTES.PROJECTS}/:projectId`}
        element={
          <PrivateRoute
            displayNotifications
            onlyImportantNotifications
            floatingNotifications
          >
            <ProjectPage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.SETTINGS}
        element={
          <PrivateRoute
            displayNavigation
            displayNotifications
          >
            <SettingsPage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.MOBILE}
        element={
          <PrivateRoute>
            <MobileDevicePage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.LOCKED}
        element={
          <PrivateRoute displayNavigation>
            <LockedOutPage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.CHECKOUT}
        element={
          <PrivateRoute>
            <CheckoutPage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.UPGRADE}
        element={
          <PrivateRoute>
            <SubscriptionPage />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.EXTERNAL_INFINITY_EXTENSION}
        element={
          <PrivateRoute
            isNavigationHidden
            hideZendeskWidget
          >
            <ExternalInfinityExtensionIframe />
          </PrivateRoute>
        }
      />
      <Route
        path={ROUTES.ROOT}
        element={
          <PrivateRoute>
            <RootPage />
          </PrivateRoute>
        }
      />
      <Route
        path="*"
        element={
          <Navigate
            to={ROUTES.ROOT}
            replace
          />
        }
      />
    </Routes>
  );
};
